import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Box,
  Button,
  Flex,
  Grid,
  Label,
  FileUpload,
  Text,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormCheckbox, FormInput } from "molecules/FormInput";
import MemoAdd from "assets/icons/Add";
import { optionFetch } from "utils/utilities";
import MemoClose from "assets/icons/Close";
import MemoClipboard from "assets/icons/Clipboard";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const validationSchema = Yup.object().shape({
  numberOfTowers: Yup.number()
    .min(0, "Value should be greater than 0")
    .typeError("Invalid number")
    .required("Number of towers is required"),
  towerPlanImages: Yup.array()
    // .of(Yup.string())
    .min(1, "Add minimum 1 image")
    .typeError("Tower image is required")
    .required("Tower image is required"),
  towers: Yup.array().of(
    Yup.object().shape({
      towerName: Yup.string()
        .typeError("Tower name is required")
        .required("Tower name is required")
        .test("check_duplicate", "Name already exists", (val, valBag) => {
          const sameTowers = valBag?.from?.[1]?.value?.towers?.filter(
            (i) => i?.towerName?.toLowerCase() === val?.toLowerCase()
          );
          return sameTowers?.length >= 2 ? false : true;
        }),
      numberOfFloors: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Number of floors is required"),
      // configurationsOfTower: Yup.number()
      //   .min(0, "Value should be greater than 0")
      //   .typeError("Invalid number")
      //   .required("Configuration of tower is required"),
      numberOfUnitsPerFloor: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Number of units per tower is required"),
      numberOfUnitsPerTower: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Units per tower is required"),
      numberOfElevatorsInTower: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Number of elevators in towers is required"),
      elevatorCrowdFactor: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Elevator crowd is required"),
      towerCloseTo: Yup.array()
        .min(1, "Choose a tower close to")
        .typeError("Tower close to is required")
        .required("Tower close to is required"),
    })
  ),
});

const defTower = {
  towerName: "",
  numberOfFloors: "",
  // configurationsOfTower: "",
  numberOfUnitsPerFloor: "",
  numberOfUnitsPerTower: "",
  numberOfElevatorsInTower: "",
  elevatorCrowdFactor: "",
  towerCloseTo: [],
};

export const TowerUnitPlansForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingTowerUnitPlan,
      createPropertyListingTowerUnitPlan,
      updatePropertyListingTowerUnitPlan,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingTowerUnitPlan(id)?.then((data) => {
        setProperty(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({ dirty, numberOfTowers, towers, towerPlanImages }) => {
      setSubmitLoading(true);
      // new nearbyAreas total

      if (dirty) {
        const formattedValues = {
          propertyId: id,
          numberOfTowers,
          towers,
          towerPlanImages,
        };
        if (property) {
          updatePropertyListingTowerUnitPlan(
            formattedValues,
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Tower unit plan updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Tower unit plan not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingTowerUnitPlan(
            formattedValues,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Tower unit plan created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Tower unit plan not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingTowerUnitPlan,
      id,
      updatePropertyListingTowerUnitPlan,
      onNext,
      rootProperty,
      property,
      fetchProperty,
    ]
  );

  // nearby area
  const [nearbyArea, setNearbyArea] = useState([]);
  useEffect(() => {
    // nearby area
    optionFetch("/nearbyareas/get", "name", "id").then((data) => {
      setNearbyArea(data);
    });
  }, []);

  return (
    <Formik
      initialValues={{
        numberOfTowers: "",
        towers: [defTower],
        towerPlanImages: [],
        ...property,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, touched, errors, dirty, submitForm }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });

        return (
          <Form>
            <Grid gridGap="2.4rem" mt="2.4rem">
              {/* <Label
                fontSize="1.8rem"
                color="secondary.700"
                astrixColor="secondary.700"
                text="Electrical"
                req
              /> */}
              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                <Grid gridRowGap="1.6rem">
                  <Label text="No. of Towers" req />
                  <FormInput
                    name="numberOfTowers"
                    // type="number"
                    placeholder="Enter Number"
                    min={0}
                    req
                    onChange={(e) => {
                      setFieldValue(
                        "numberOfTowers",
                        parseInt(e?.target?.value) || 0
                      );

                      if (e?.target?.value > values?.towers?.length) {
                        const duplicate = [
                          ...JSON.parse(JSON.stringify(values?.towers)),
                          ...new Array(
                            e?.target?.value - values?.towers?.length
                          ).fill(JSON.parse(JSON.stringify(defTower))),
                        ];

                        setFieldValue("towers", duplicate);
                      }
                      if (e?.target?.value < values?.towers?.length) {
                        let duplicate = JSON.parse(
                          JSON.stringify(values?.towers)
                        );
                        duplicate = duplicate.slice(0, e?.target?.value);

                        setFieldValue("towers", duplicate);
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <Box>
                <Label text="Upload Tower Pictures" mb="1.6rem" req />
                <FileUpload
                  files={values?.towerPlanImages || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.towerPlanImages
                        ? JSON.parse(JSON.stringify(values?.towerPlanImages)) ||
                          []
                        : []),
                      ...locations?.map((i) => ({ image: i, alt: "" })),
                    ];

                    setFieldValue("towerPlanImages", newProjectImages);
                  }}
                  onSubmitDescription={({ alt, file }) => {
                    setFieldValue(`towerPlanImages.[${file?.index}].alt`, alt);
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.towerPlanImages
                        ? JSON.parse(JSON.stringify(values?.towerPlanImages)) ||
                          []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("towerPlanImages", newProjectImages);
                  }}
                />
                {touched?.towerPlanImages && errors?.towerPlanImages && (
                  <ErrorMsg text={errors?.towerPlanImages} />
                )}
              </Box>

              {[...values?.towers].map((item, index) => {
                return (
                  <Grid
                    gridGap="2.4rem"
                    mt="2.4rem"
                    key={index}
                    p="2.4rem"
                    border="1px solid"
                    borderColor="neutral.100"
                    borderRadius="1rem"
                  >
                    <Flex jC="space-between">
                      {/* <Label
                        fontSize="1.8rem"
                        color="secondary.700"
                        astrixColor="secondary.700"
                        text={`Tower ${alphaNumericIndexing(index)}`}
                      /> */}
                      <Grid gridRowGap="1.6rem">
                        <Label text="Tower Name" req />
                        <FormInput
                          name={`towers.[${index}].towerName`}
                          type="text"
                          placeholder="Enter Name"
                          req
                        />
                      </Grid>
                      <Flex aI="start">
                        <Flex
                          alignItems="center"
                          color="secondary.700"
                          cursor="pointer"
                          onClick={() => {
                            let duplicate = JSON.parse(
                              JSON.stringify(values?.towers)
                            );

                            duplicate = [
                              ...duplicate?.slice(0, index),
                              JSON.parse(JSON.stringify(duplicate[index])),
                              ...duplicate?.slice(index),
                            ];

                            setFieldValue("towers", duplicate);
                          }}
                        >
                          <MemoClipboard height="2rem" width="2rem" />
                          <Text
                            color="secondary.700"
                            fontFamily="NeometricBold"
                            ml="1rem"
                          >
                            Duplicate
                          </Text>
                        </Flex>
                        <Flex
                          alignItems="center"
                          color="accents.100"
                          cursor="pointer"
                          ml="1.8rem"
                          onClick={() => {
                            let duplicate = JSON.parse(
                              JSON.stringify(values?.towers)
                            );

                            duplicate = [
                              ...duplicate?.slice(0, index),
                              ...duplicate?.slice(index + 1),
                            ];

                            setFieldValue("towers", duplicate);
                          }}
                        >
                          <MemoClose height="1.3rem" width="1.3rem" />
                          <Text
                            color="accents.100"
                            fontFamily="NeometricBold"
                            ml="1rem"
                          >
                            Remove
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                    <Grid
                      gridTemplateColumns="1fr 1fr"
                      gridColumnGap="2.4rem"
                      gridRowGap="1.6rem"
                    >
                      <Grid gridRowGap="1.6rem">
                        <Label text="No. of Floors" req />
                        <FormInput
                          name={`towers.[${index}].numberOfFloors`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid>
                      {/* <Grid gridRowGap="1.6rem">
                        <Label text="Configurations of Tower" req />
                        <FormInput
                          name={`towers.[${index}].configurationsOfTower`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid> */}
                    </Grid>
                    <Grid
                      gridTemplateColumns="1fr 1fr"
                      gridColumnGap="2.4rem"
                      gridRowGap="1.6rem"
                    >
                      <Grid gridRowGap="1.6rem">
                        <Label text="No. of Units per Floor" req />
                        <FormInput
                          name={`towers.[${index}].numberOfUnitsPerFloor`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid>
                      <Grid gridRowGap="1.6rem">
                        <Label text="No. of Units per Tower" req />
                        <FormInput
                          name={`towers.[${index}].numberOfUnitsPerTower`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      gridTemplateColumns="1fr 1fr"
                      gridColumnGap="2.4rem"
                      gridRowGap="1.6rem"
                    >
                      <Grid gridRowGap="1.6rem">
                        <Label text="No of Elevators in Tower" req />
                        <FormInput
                          name={`towers.[${index}].numberOfElevatorsInTower`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid>
                      <Grid gridRowGap="1.6rem">
                        <Label text="Elevator Crowd Factor" req />
                        <FormInput
                          name={`towers.[${index}].elevatorCrowdFactor`}
                          // type="number"
                          placeholder="Enter Number"
                          req
                        />
                      </Grid>
                    </Grid>

                    <Grid gridRowGap="2.4rem">
                      <Label text="Tower Close to" req />
                      <Flex gridGap="3rem" flexWrap="wrap">
                        {nearbyArea?.map((item, cindex) => (
                          <FormCheckbox
                            key={cindex}
                            name={`towers.[${index}].towerCloseTo`}
                            height="1.8rem"
                            label={item?.label}
                            value={item?.value}
                          />
                        ))}
                      </Flex>
                      {touched?.towers?.[index]?.towerCloseTo &&
                        errors?.towers?.[index]?.towerCloseTo && (
                          <ErrorMsg
                            text={errors?.towers?.[index]?.towerCloseTo}
                          />
                        )}
                    </Grid>
                  </Grid>
                );
              })}
              <Flex
                alignItems="center"
                color="secondary.700"
                cursor="pointer"
                onClick={() => {
                  const duplicate = JSON.parse(JSON.stringify(values?.towers));
                  duplicate.push(JSON.parse(JSON.stringify(defTower)));

                  setFieldValue("towers", duplicate);
                  setFieldValue("numberOfTowers", duplicate?.length);
                }}
              >
                <MemoAdd height="1.3rem" width="1.3rem" />
                <Text
                  color="secondary.700"
                  fontFamily="NeometricBold"
                  ml="1rem"
                >
                  Add More
                </Text>
              </Flex>

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
